// src/pages/events/Events.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Avatar,
  Divider,
  Stack,
  LinearProgress,
  Paper,
  Tab,
  Tabs
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Euro as EuroIcon,
  Payment as PaymentIcon,
  Photo as PhotoIcon,
  ConfirmationNumber as TicketIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../../firebase';
import StorageImage from '../../shared/StorageImage';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import EventVolunteersDialog from './EventVolunteersDialog';
import EventAttendeesDialog from './EventAttendeesDialog';
import EventPaymentsDialog from './EventPaymentsDialog';
import EventGalleryDialog from './EventGalleryDialog';

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState(0); // 0=todos 1=próximos 2=pasados
  
  // Estados para los diálogos
  const [volunteersDialogOpen, setVolunteersDialogOpen] = useState(false);
  const [attendeesDialogOpen, setAttendeesDialogOpen] = useState(false);
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    let result = events;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(e =>
        e.titulo?.toLowerCase().includes(term) ||
        e.descripcion?.toLowerCase().includes(term) ||
        e.ubicacion?.toLowerCase().includes(term) ||
        e.categoria?.toLowerCase().includes(term)
      );
    }
    if (filterTab === 1) result = result.filter(e => !isPastEvent(e));
    if (filterTab === 2) result = result.filter(e => isPastEvent(e));
    setFilteredEvents(result);
  }, [searchTerm, events, filterTab]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      // Obtener todos los eventos y ordenarlos por fecha en el cliente
      const q = query(collection(db, 'events'));
      const querySnapshot = await getDocs(q);
      const eventsData = [];
      
      querySnapshot.forEach((doc) => {
        const eventData = { id: doc.id, ...doc.data() };
        eventsData.push(eventData);
      });
      
      // Ordenar eventos por fecha de inicio (más recientes primero)
      eventsData.sort((a, b) => {
        const dateA = getEventDate(a.fechaInicio);
        const dateB = getEventDate(b.fechaInicio);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        return dateB.getTime() - dateA.getTime();
      });
      
      setEvents(eventsData);
      setFilteredEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
      showSnackbar('Error al cargar los eventos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener la fecha del evento (compatible con ambos formatos)
  const getEventDate = (fechaInicio) => {
    if (!fechaInicio) return null;
    
    // Si es un Timestamp de Firestore (formato anterior)
    if (fechaInicio.seconds) {
      return new Date(fechaInicio.seconds * 1000);
    }
    
    // Si es un string datetime-local (formato nuevo)
    if (typeof fechaInicio === 'string') {
      try {
        const parsedDate = parseISO(fechaInicio);
        return isValid(parsedDate) ? parsedDate : null;
      } catch (error) {
        console.error('Error parsing date string:', fechaInicio);
        return null;
      }
    }
    
    // Si es un objeto Date
    if (fechaInicio instanceof Date) {
      return fechaInicio;
    }
    
    return null;
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este evento? Esta acción eliminará el evento. Los pagos y registros asociados se mantendrán en el sistema.')) {
      try {
        // Eliminar el documento
        await deleteDoc(doc(db, 'events', eventId));
        
        // Actualizar el estado local
        setEvents(events.filter(event => event.id !== eventId));
        showSnackbar('Evento eliminado correctamente', 'success');
      } catch (error) {
        console.error('Error deleting event:', error);
        showSnackbar('Error al eliminar el evento', 'error');
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Funciones para abrir los diferentes diálogos
  const handleOpenVolunteersDialog = (event) => {
    setSelectedEvent(event);
    setVolunteersDialogOpen(true);
  };

  const handleOpenAttendeesDialog = (event) => {
    setSelectedEvent(event);
    setAttendeesDialogOpen(true);
  };

  const handleOpenPaymentsDialog = (event) => {
    setSelectedEvent(event);
    setPaymentsDialogOpen(true);
  };

  const handleOpenGalleryDialog = (event) => {
    setSelectedEvent(event);
    setGalleryDialogOpen(true);
  };

  // Funciones para cerrar los diálogos
  const handleCloseVolunteersDialog = () => {
    setVolunteersDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleCloseAttendeesDialog = () => {
    setAttendeesDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleClosePaymentsDialog = () => {
    setPaymentsDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleCloseGalleryDialog = () => {
    setGalleryDialogOpen(false);
    setSelectedEvent(null);
    // Recargar eventos para actualizar la galería si se modificó
    fetchEvents();
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const isPastEvent = (event) => {
    const eventDate = getEventDate(event.fechaInicio);
    if (!eventDate) return false;
    return eventDate < new Date();
  };

  const formatEventDate = (fechaInicio) => {
    const eventDate = getEventDate(fechaInicio);
    
    if (!eventDate) {
      return 'Fecha no especificada';
    }
    
    try {
      return format(eventDate, "dd 'de' MMMM 'de' yyyy", { locale: es });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Fecha inválida';
    }
  };

  const getCategoryColor = (categoria) => {
    const colors = {
      'cultural': '#9C27B0',
      'deportivo': '#4CAF50',
      'educativo': '#2196F3',
      'familiar': '#FF9800',
      'festivo': '#F44336',
      'general': '#757575'
    };
    return colors[categoria] || colors.general;
  };

  const getCategoryName = (categoria) => {
    const names = {
      'cultural': 'Cultural',
      'deportivo': 'Deportivo',
      'educativo': 'Educativo',
      'familiar': 'Familiar',
      'festivo': 'Festivo',
      'general': 'General'
    };
    return names[categoria] || 'General';
  };

  const paidEvents   = filteredEvents.filter(e => Number(e.precio) > 0);
  const freeEvents   = filteredEvents.filter(e => !e.precio || Number(e.precio) === 0);
  const upcomingCount = events.filter(e => !isPastEvent(e)).length;
  const pastCount     = events.filter(e =>  isPastEvent(e)).length;

  // Función de render (no componente) — evita remounting en cada render del padre
  const renderCard = (event) => {
    const past     = isPastEvent(event);
    const isPaid   = Number(event.precio) > 0;
    const sold     = event.sold || 0;
    const total    = event.totalpass || 0;
    const pct      = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
    const catColor = getCategoryColor(event.categoria);

    return (
      <Card key={event.id} sx={{
        width: '100%', display: 'flex', flexDirection: 'column',
        opacity: past ? 0.82 : 1,
        border: isPaid ? '1px solid #f59e0b40' : '1px solid transparent',
        transition: 'transform .2s, box-shadow .2s',
        '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 }
      }}>
        {/* ── Imagen / placeholder ── */}
        <Box sx={{
          position: 'relative', width: '100%', height: 160,
          overflow: 'hidden', flexShrink: 0, bgcolor: catColor + '22'
        }}>
          {event.imagen
            ? <StorageImage
                src={event.imagen}
                alt={event.titulo}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            : <Box sx={{
                width: '100%', height: '100%',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                background: `linear-gradient(135deg, ${catColor}55 0%, ${catColor}18 100%)`
              }}>
                <EventIcon sx={{ fontSize: 48, color: catColor, opacity: 0.7, mb: 0.5 }} />
                <Typography variant="caption" fontWeight={800}
                  sx={{ color: catColor, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                  {getCategoryName(event.categoria)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, opacity: 0.75 }}>
                  {formatEventDate(event.fechaInicio)}
                </Typography>
              </Box>
          }
          {/* Badges izquierda */}
          <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Chip label={getCategoryName(event.categoria)} size="small"
              sx={{ bgcolor: catColor, color: '#fff', fontWeight: 700, fontSize: 11 }} />
            {isPaid && (
              <Chip icon={<EuroIcon sx={{ fontSize: '13px !important' }} />}
                label={`${Number(event.precio).toFixed(2)} €`} size="small"
                sx={{ bgcolor: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 11 }} />
            )}
          </Box>
          {/* Estado derecha */}
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            {past
              ? <Chip label="Finalizado" size="small"
                  sx={{ bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11 }} />
              : <Chip
                  icon={event.active
                    ? <CheckCircleIcon sx={{ fontSize: '13px !important' }} />
                    : <CancelIcon sx={{ fontSize: '13px !important' }} />}
                  label={event.active ? 'Activo' : 'Inactivo'}
                  color={event.active ? 'success' : 'default'} size="small" />
            }
          </Box>
        </Box>

        {/* ── Contenido ── */}
        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
          <Box sx={{ height: 52, overflow: 'hidden', mb: 1 }}>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {event.titulo}
            </Typography>
          </Box>

          <Stack spacing={0.75} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CalendarIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                {formatEventDate(event.fechaInicio)}
              </Typography>
            </Box>
            {event.ubicacion && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <LocationIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" noWrap>
                  {event.ubicacion}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Barra de tickets — solo eventos de pago */}
          {isPaid && (
            <Box sx={{ p: 1.5, bgcolor: '#fef3c720', borderRadius: 1.5, border: '1px solid #f59e0b30' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="caption" fontWeight={600} color="#b45309">
                  <TicketIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                  {sold} / {total || '∞'} entradas
                </Typography>
                <Typography variant="caption" fontWeight={700} color="success.main">
                  {(sold * Number(event.precio)).toFixed(0)} €
                </Typography>
              </Box>
              {total > 0 && (
                <LinearProgress variant="determinate" value={pct} sx={{
                  height: 6, borderRadius: 3, bgcolor: '#f59e0b20',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: pct >= 100 ? 'error.main' : '#f59e0b', borderRadius: 3
                  }
                }} />
              )}
            </Box>
          )}

          <Box sx={{ height: 36, overflow: 'hidden', mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.5 }}>
              {event.descripcion}
            </Typography>
          </Box>
        </CardContent>

        <Divider />

        {/* ── Acciones ── */}
        <CardActions sx={{ px: 1.5, py: 1, justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ver asistentes">
              <Chip clickable size="small" icon={<PeopleIcon />}
                label={event.asistentes?.length || 0}
                onClick={() => handleOpenAttendeesDialog(event)} />
            </Tooltip>
            <Tooltip title="Ver voluntarios">
              <Chip clickable size="small" icon={<GroupIcon />}
                label={event.voluntarios?.length || 0}
                onClick={() => handleOpenVolunteersDialog(event)} />
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={0}>
            {past && (
              <Tooltip title="Galería de fotos">
                <IconButton size="small" color="primary" onClick={() => handleOpenGalleryDialog(event)}>
                  <PhotoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isPaid && (
              <Tooltip title="Ver pagos">
                <IconButton size="small" sx={{ color: '#f59e0b' }} onClick={() => handleOpenPaymentsDialog(event)}>
                  <PaymentIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => navigate(`/admin/events/${event.id}/edit`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton size="small" color="error" onClick={() => handleDeleteEvent(event.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardActions>
      </Card>
    );
  };

  const renderGrid = (evts) => (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(4, 1fr)'
      },
      gap: 2.5
    }}>
      {evts.map(e => (
        <Box key={e.id} sx={{ display: 'flex', minWidth: 0 }}>
          {renderCard(e)}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>

      {/* ── Cabecera ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Gestión de Eventos</Typography>
          <Stack direction="row" spacing={1.5} mt={0.5}>
            <Typography variant="caption" color="text.secondary">{events.length} total</Typography>
            <Typography variant="caption" color="success.main">· {upcomingCount} próximos</Typography>
            <Typography variant="caption" color="text.disabled">· {pastCount} finalizados</Typography>
          </Stack>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => navigate('/admin/events/new')} sx={{ fontWeight: 700 }}>
          Nuevo evento
        </Button>
      </Box>

      {/* ── Búsqueda + tabs ── */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <TextField fullWidth size="small" variant="outlined"
            placeholder="Buscar por título, descripción, ubicación o categoría…"
            value={searchTerm} onChange={handleSearchChange}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} />
        </Box>
        <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)} sx={{ px: 1 }}>
          <Tab label={`Todos (${events.length})`} />
          <Tab label={`Próximos (${upcomingCount})`} />
          <Tab label={`Finalizados (${pastCount})`} />
        </Tabs>
      </Paper>

      {/* ── Contenido ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredEvents.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <EventIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'Sin resultados para esa búsqueda' : 'No hay eventos'}
          </Typography>
          {!searchTerm && (
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => navigate('/admin/events/new')} sx={{ mt: 2 }}>
              Crear primer evento
            </Button>
          )}
        </Box>
      ) : (
        <Box>
          {/* Sección eventos de PAGO */}
          {paidEvents.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 4, height: 24, bgcolor: '#f59e0b', borderRadius: 2 }} />
                <Typography variant="h6" fontWeight={700}>Eventos de Pago</Typography>
                <Chip label={paidEvents.length} size="small"
                  sx={{ bgcolor: '#f59e0b', color: '#fff', fontWeight: 700 }} />
              </Box>
              {renderGrid(paidEvents)}
            </Box>
          )}

          {/* Sección eventos GRATUITOS */}
          {freeEvents.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box sx={{ width: 4, height: 24, bgcolor: '#22c55e', borderRadius: 2 }} />
                <Typography variant="h6" fontWeight={700}>Eventos Gratuitos</Typography>
                <Chip label={freeEvents.length} size="small"
                  sx={{ bgcolor: '#22c55e', color: '#fff', fontWeight: 700 }} />
              </Box>
              {renderGrid(freeEvents)}
            </Box>
          )}
        </Box>
      )}

      {/* Diálogos */}
      {selectedEvent && (
        <>
          <EventVolunteersDialog
            open={volunteersDialogOpen}
            onClose={handleCloseVolunteersDialog}
            eventId={selectedEvent.id}
            eventTitle={selectedEvent.titulo}
          />
          
          <EventAttendeesDialog
            open={attendeesDialogOpen}
            onClose={handleCloseAttendeesDialog}
            eventId={selectedEvent.id}
            eventTitle={selectedEvent.titulo}
          />

          {selectedEvent.precio > 0 && (
            <EventPaymentsDialog
              open={paymentsDialogOpen}
              onClose={handleClosePaymentsDialog}
              eventId={selectedEvent.id}
              eventTitle={selectedEvent.titulo}
              eventPrice={selectedEvent.precio}
            />
          )}

          {isPastEvent(selectedEvent) && (
            <EventGalleryDialog
              open={galleryDialogOpen}
              onClose={handleCloseGalleryDialog}
              eventId={selectedEvent.id}
              eventTitle={selectedEvent.titulo}
              currentGallery={selectedEvent.galeria || []}
            />
          )}
        </>
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Events;